import React from "react";
import { Button, Col, Form, Input, Row, Select, Tooltip, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { findDuplicateRouteNames, slugifyRouteName } from "./routeNames";
import { getAvailableBranches } from "./functions";

// Operadores do desvio: comparação de texto simples (com trim), sem coerção
// de tipo — espelha o contrato do backend.
const OPERATORS = [
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Não é igual a" },
    { value: "in", label: "Está na lista" },
    { value: "not_in", label: "Não está na lista" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
];

const UNARY_OPERATORS = ["is_empty", "is_not_empty"];
const LIST_OPERATORS = ["in", "not_in"];

/**
 * Chave da comparação de um caminho — mesma regra que o save aplica no
 * backend: dois caminhos com a mesma chave casam com exatamente os mesmos
 * valores. `null` quando ainda não há valor comparado (caminho em edição).
 */
const conditionKey = (route) => {
    const operator = route?.condition ?? "equals";

    if (UNARY_OPERATORS.includes(operator)) {
        return operator;
    }

    if (LIST_OPERATORS.includes(operator)) {
        // Lista é conjunto: ordem e repetição não mudam o que o caminho casa.
        const items = Array.isArray(route?.referenceValue) ? route.referenceValue : [];
        const values = [
            ...new Set(items.map((item) => String(item ?? "").trim()).filter(Boolean)),
        ].sort();
        return values.length ? `${operator}:${JSON.stringify(values)}` : null;
    }

    const value = String(route?.referenceValue ?? "").trim();
    return value ? `${operator}:${value}` : null;
};

/** Índices dos caminhos que repetem a comparação de outro caminho. */
const findDuplicateRoutes = (routes) => {
    const firstByKey = new Map();
    const duplicates = new Set();

    routes.forEach((route, index) => {
        const key = conditionKey(route);
        if (!key) return;
        if (firstByKey.has(key)) {
            duplicates.add(firstByKey.get(key));
            duplicates.add(index);
        } else {
            firstByKey.set(key, index);
        }
    });

    return duplicates;
};

/**
 * Índices dos caminhos cujo identificador repete o de outro caminho. Editar
 * rótulos não chega mais a esse estado — o identificador só acompanha o rótulo
 * quando o resultado está livre —, mas automações salvas antes dessa regra
 * podem ter chegado, e aí os passos dos dois caminhos são indistinguíveis.
 */
const findDuplicateNameRoutes = (routes) => {
    const duplicatedNames = findDuplicateRouteNames(routes);
    const duplicates = new Set();

    routes.forEach((route, index) => {
        if (route?.name && duplicatedNames.has(route.name)) {
            duplicates.add(index);
        }
    });

    return duplicates;
};

/**
 * Índices dos caminhos cujo rótulo repete o de outro caminho. Não quebra a
 * automação — o vínculo dos passos é o identificador, não o rótulo —, mas
 * deixa dois caminhos indistinguíveis no fluxo.
 */
const findDuplicateLabelRoutes = (routes) => {
    const firstByLabel = new Map();
    const duplicates = new Set();

    routes.forEach((route, index) => {
        const label = String(route?.label ?? "").trim().toLowerCase();
        if (!label) return;
        if (firstByLabel.has(label)) {
            duplicates.add(firstByLabel.get(label));
            duplicates.add(index);
        } else {
            firstByLabel.set(label, index);
        }
    });

    return duplicates;
};

const emptyRoute = () => ({
    name: "",
    label: "",
    condition: "equals",
    referenceValue: "",
});

/**
 * Editor da lista de rotas do passo "Desvio" (`config.routes`).
 * Recebe value/onChange do Form.Item que o BetaSchemaForm cria.
 */
export const SwitchRoutesEditor = ({ value, onChange, onBlur }) => {
    const { actions, actionStep } = useSelector(({ automation }) => automation);
    const routes = Array.isArray(value) ? value : [];
    const duplicatedRoutes = findDuplicateRoutes(routes);
    const duplicatedNameRoutes = findDuplicateNameRoutes(routes);
    const duplicatedLabelRoutes = findDuplicateLabelRoutes(routes);

    // Caminhos já declarados antes deste desvio, no mesmo escopo dele: uma rota
    // com um desses identificadores seria indistinguível da que já existe.
    const prefix = actions?.[actionStep]?.branch
        ? `${actions[actionStep].branch}.`
        : "";
    const declaredBefore = new Set(
        getAvailableBranches(actions ?? [], actionStep).map((item) => item.value)
    );

    const updateRoute = (index, patch) => {
        const newRoutes = routes.map((route, i) =>
            i === index ? { ...route, ...patch } : route
        );
        onChange(newRoutes);
    };

    const onChangeLabel = (index, label) => {
        const nextName = slugifyRouteName(label);
        const takenNames = new Set(
            routes
                .filter((_, i) => i !== index)
                .map((route) => route?.name)
                .filter(Boolean)
        );
        // O identificador acompanha o rótulo, mas só enquanto puder fazê-lo sem
        // desligar os passos do caminho — o `branch` deles guarda esse
        // identificador e mais nada. Um rótulo que não gera identificador
        // (apagado, só com símbolos) ou que geraria um já ocupado — por outro
        // caminho deste desvio ou por um caminho declarado antes dele — deixa o
        // identificador como está: o rótulo digitado aparece normalmente, os
        // passos continuam no lugar, e o identificador volta a acompanhar assim
        // que o rótulo produzir um que esteja livre.
        const isFree =
            nextName &&
            !takenNames.has(nextName) &&
            !declaredBefore.has(prefix + nextName);

        updateRoute(index, {
            label,
            ...(isFree ? { name: nextName } : {}),
        });
    };

    const onChangeOperator = (index, condition) => {
        const patch = { condition };
        const wasList = LIST_OPERATORS.includes(routes[index]?.condition);
        const isList = LIST_OPERATORS.includes(condition);
        if (wasList !== isList) {
            patch.referenceValue = isList ? [] : "";
        }
        updateRoute(index, patch);
    };

    const addRoute = () => onChange([...routes, emptyRoute()]);

    const removeRoute = (index) =>
        onChange(routes.filter((_, i) => i !== index));

    const renderReferenceValue = (route, index) => {
        if (UNARY_OPERATORS.includes(route.condition)) {
            return null;
        }
        if (LIST_OPERATORS.includes(route.condition)) {
            return (
                <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Digite os valores e pressione Enter"
                    value={Array.isArray(route.referenceValue) ? route.referenceValue : []}
                    onChange={(list) => updateRoute(index, { referenceValue: list })}
                    onBlur={onBlur}
                    open={false}
                    suffixIcon={null}
                />
            );
        }
        return (
            <Input
                placeholder="Valor comparado"
                value={typeof route.referenceValue === "string" ? route.referenceValue : ""}
                onChange={(event) =>
                    updateRoute(index, { referenceValue: event.target.value })
                }
                onBlur={onBlur}
            />
        );
    };

    // Cada caminho vira um bloco empilhado (nome em cima, comparação embaixo).
    // A grade de 4 colunas anterior não cabe na largura do inspector e obrigava
    // a ler a regra na horizontal, campo a campo.
    return (
        <div className="ae-panel">
            <div className="ae-panel__head">
                <span className="ae-panel__title">Caminhos do desvio</span>
                <Button icon={<PlusOutlined />} onClick={addRoute} size="small" type="dashed">
                    Adicionar
                </Button>
            </div>
            <div className="ae-panel__body">
                <div className="ae-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                    O valor avaliado é comparado com cada caminho abaixo, de cima para
                    baixo. Quando uma comparação casa, a automação segue por aquele
                    caminho — e só executa os passos adicionados nele (use o “+” do
                    caminho, no fluxo ao lado). Se o valor for um campo de múltipla
                    escolha, cada opção selecionada é comparada: o caminho casa quando
                    qualquer uma delas satisfaz a comparação.
                </div>
                {routes.length < 2 && (
                    <Typography.Paragraph type="warning" style={{ fontSize: 12 }}>
                        Um desvio precisa de pelo menos 2 caminhos.
                    </Typography.Paragraph>
                )}
                {duplicatedRoutes.size > 0 && (
                    <Typography.Paragraph type="danger" style={{ fontSize: 12 }}>
                        Os caminhos {[...duplicatedRoutes].map((index) => index + 1).join(", ")} têm
                        a mesma comparação. Cada caminho precisa de uma condição diferente — ajuste
                        antes de salvar.
                    </Typography.Paragraph>
                )}
                {duplicatedNameRoutes.size > 0 && (
                    <Typography.Paragraph type="danger" style={{ fontSize: 12 }}>
                        Os caminhos {[...duplicatedNameRoutes].map((index) => index + 1).join(", ")} têm
                        o mesmo identificador interno, e por isso não é possível saber a qual deles
                        cada passo pertence. Renomeie um deles e confira os passos de ambos antes de
                        salvar.
                    </Typography.Paragraph>
                )}
                {duplicatedLabelRoutes.size > 0 && (
                    <Typography.Paragraph type="warning" style={{ fontSize: 12 }}>
                        Os caminhos {[...duplicatedLabelRoutes].map((index) => index + 1).join(", ")} têm
                        o mesmo nome. Os passos de cada um continuam no seu caminho, mas os dois
                        ficam iguais no fluxo — vale diferenciar os nomes.
                    </Typography.Paragraph>
                )}
                {routes.length === 0 && (
                    <div className="ae-empty">Nenhum caminho definido ainda.</div>
                )}
                {routes.map((route, index) => (
                    <div
                        className={`ae-route${
                            duplicatedRoutes.has(index) || duplicatedNameRoutes.has(index)
                                ? " ae-route--invalid"
                                : ""
                        }`}
                        key={index}
                    >
                        <div className="ae-route__head">
                            <span className="ae-route__index">Caminho {index + 1}</span>
                            <Button
                                icon={<DeleteOutlined />}
                                onClick={() => removeRoute(index)}
                                danger
                                type="text"
                                size="small"
                            />
                        </div>
                        <div className="ae-field">
                            <label className="ae-label">Nome do caminho</label>
                            <Tooltip title={`Identificador interno: ${route.name || "(derivado do nome)"}`}>
                                <Input
                                    placeholder="Ex.: Aprovado"
                                    value={route.label ?? ""}
                                    onChange={(event) => onChangeLabel(index, event.target.value)}
                                    onBlur={onBlur}
                                />
                            </Tooltip>
                        </div>
                        <div className="ae-field">
                            <label className="ae-label">Seguir por aqui se o valor…</label>
                            <Row gutter={8}>
                                <Col span={UNARY_OPERATORS.includes(route.condition) ? 24 : 10}>
                                    <Select
                                        style={{ width: "100%" }}
                                        options={OPERATORS}
                                        value={route.condition ?? "equals"}
                                        onChange={(condition) => onChangeOperator(index, condition)}
                                    />
                                </Col>
                                {!UNARY_OPERATORS.includes(route.condition) && (
                                    <Col span={14}>
                                        {renderReferenceValue(route, index)}
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// O que fazer quando o valor avaliado não casa com nenhum caminho.
const FALLBACK_ACTIONS = [
    {
        value: "continue",
        label: "Continuar no fluxo principal",
        hint: "Os passos dos caminhos são pulados e a automação segue no primeiro passo fora do desvio.",
    },
    {
        value: "route",
        label: "Seguir por um caminho",
        hint: "O caminho escolhido executa como se a comparação dele tivesse casado.",
    },
    {
        value: "abort",
        label: "Encerrar a automação (sem erro)",
        hint: "A automação termina no desvio: nenhum passo seguinte executa, nem os que estão fora dos caminhos.",
    },
];

/**
 * Editor do fallback do desvio (`config.fallback`): o que fazer quando
 * nenhuma rota casa. O padrão continua sendo `abort`, o comportamento de
 * todos os desvios já salvos.
 */
export const SwitchFallbackEditor = ({ value, onChange }) => {
    const form = Form.useFormInstance();
    const routes = Form.useWatch("routes", form) ?? [];

    const action = value?.action ?? "abort";

    const routeOptions = routes
        .filter((route) => route && route.name)
        .map((route) => ({
            value: route.name,
            label: route.label || route.name,
        }));

    const hint = FALLBACK_ACTIONS.find((item) => item.value === action)?.hint;

    return (
        <div>
            <Select
                style={{ width: "100%" }}
                value={action}
                onChange={(newAction) =>
                    onChange(
                        newAction === "route"
                            ? { action: "route", name: value?.name ?? null }
                            : { action: newAction }
                    )
                }
                options={FALLBACK_ACTIONS.map(({ value: optionValue, label }) => ({
                    value: optionValue,
                    label,
                }))}
            />
            {action === "route" && (
                <Select
                    style={{ width: "100%", marginTop: 8 }}
                    placeholder="Escolha o caminho"
                    value={value?.name ?? undefined}
                    onChange={(name) => onChange({ action: "route", name })}
                    options={routeOptions}
                />
            )}
            {hint && <div className="ae-hint">{hint}</div>}
        </div>
    );
};

